import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';
import { queryKeys } from '@/lib/query/client';

import type {
  BucketView,
  CashflowFilters,
  GapAnalysisDetail,
  GapAnalysisResponse,
  TriggerCalculationRequest,
} from './types';

/**
 * GET /api/v1/alm/gaps?date=YYYY-MM-DD&view=STANDARD|SHORT_MEDIUM_LONG
 *
 * Returns the latest gap result for the active tenant. `view` (Phase 2.2)
 * picks the bucket-fold granularity server-side; STANDARD is the atomic
 * 7-bucket grid and back-compat default.
 */
export function useLatestGap(
  date: string | undefined,
  view: BucketView = 'STANDARD',
  options?: Omit<
    UseQueryOptions<GapAnalysisDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: ['alm', 'gaps', 'latest', date, view],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (view && view !== 'STANDARD') params.set('view', view);
      const qs = params.toString();
      const url = qs ? `${apiPath('alm', 'gaps')}?${qs}` : apiPath('alm', 'gaps');
      // Server may legitimately return data:null when no calc has run yet.
      const data = await apiGet<GapAnalysisDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * GET /api/v1/alm/gaps/slice — Phase 2.2.B
 *
 * Ephemeral filtered gap calculation. Re-runs the engine in-memory over a
 * filtered cashflow set; nothing persisted. Same response shape as
 * /api/v1/alm/gaps so the same `GapAnalysisDetail` consumer renders both.
 *
 * Caller decides when to use this hook vs {@link useLatestGap} based on
 * whether any filter dimension is set.
 */
export function useSlicedGap(
  date: string | undefined,
  filters: CashflowFilters,
  view: BucketView = 'STANDARD',
  options?: Omit<
    UseQueryOptions<GapAnalysisDetail | null, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: ['alm', 'gaps', 'slice', date, view, filters],
    enabled: Boolean(date),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('date', date!);
      if (view && view !== 'STANDARD')      params.set('view', view);
      if (filters.currency)                  params.set('currency', filters.currency);
      if (filters.instrumentType)            params.set('instrumentType', filters.instrumentType);
      if (filters.cfType)                    params.set('cfType', filters.cfType);
      if (filters.instrument)                params.set('instrument', filters.instrument);
      const url = `${apiPath('alm', 'gaps', 'slice')}?${params.toString()}`;
      const data = await apiGet<GapAnalysisDetail | null>(url);
      return data ?? null;
    },
    ...options,
  });
}

/**
 * POST /api/v1/alm/gaps/calculate — triggers an on-demand calculation.
 *
 * Server returns 202 Accepted with a `GapAnalysisResponse` (lightweight,
 * no buckets). When cx-reporting + WebSocket land, the page will subscribe
 * to `result.resultId` to receive the full detail.
 */
export function useTriggerCalculation(
  options?: UseMutationOptions<GapAnalysisResponse, CubixApiError, TriggerCalculationRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiPost<GapAnalysisResponse, TriggerCalculationRequest>(
        apiPath('alm', 'gaps', 'calculate'),
        body,
      ),
    ...options,
    onSuccess: (...args) => {
      // Bust the latest-gap cache so the next read picks up the new result.
      qc.invalidateQueries({ queryKey: queryKeys.alm.all });
      return options?.onSuccess?.(...args);
    },
  });
}
